import { isEmpty, isNil } from 'lodash'
import loSto from '../config/loSto'
import { logger } from '../lib/logging'
import pubSub from '../lib/util/pubSub'
import Process from '../lib/util/Process'
import { getTokenExpiryInSeconds } from '../lib'
import fhirTokenCacheReader from '../readers/fhirTokenCache'
import emrIntegrationService from '../services/emrIntegration'
import { fetchEmrFhirTokens, updateEmrFhirTokens } from '../rest'
import { LO_STO, EMR_INTEGRATION_STATUS, SECONDARY_EMR_TYPE } from '../lib/constants'
import { EMR_INTEGRATION_DETAILS_UPDATE } from '../constants/pubSub.events'
import { getEpicTokenRefreshHandler, getCernerTokenRefreshHandler } from '../helpers/emrIntegration'

// If Access token from the cache does not have enough time left
// we should get a new access token through refresh handler
// hence taking the buffer time as 5 minutes(300)
const VALID_ACCESS_TOKEN_TIME_BUFFER = 300

const ehrRefreshTokenHandler = (emr, refreshToken, expiryTime) => {
  // EPIC
  if (emr === SECONDARY_EMR_TYPE.EPIC_EMR) {
    const epicTokenRefreshHandler = getEpicTokenRefreshHandler(refreshToken, expiryTime)
    epicTokenRefreshHandler()
  }
  // CERNER
  if (emr === SECONDARY_EMR_TYPE.CERNER_EMR) {
    const cernerTokenRefreshHandler = getCernerTokenRefreshHandler(refreshToken, expiryTime)
    cernerTokenRefreshHandler()
  }
}

const updateEmrIntegrationDetails = (emrIntegrationDetails, accessToken, refreshToken, expiryTime) => {
  loSto.set(LO_STO.FHIR_ACCESS_TOKEN, accessToken)
  updateEmrFhirTokens(emrIntegrationDetails.emr, accessToken)
  loSto.set(LO_STO.EMR_INTEGRATION_DETAILS, {
    ...emrIntegrationDetails,
    status: EMR_INTEGRATION_STATUS.CONNECTED
  })
  pubSub.publish(EMR_INTEGRATION_DETAILS_UPDATE)
  ehrRefreshTokenHandler(emrIntegrationDetails.emr, refreshToken, expiryTime)
}

const isValidAccessToken = (next, params = {}) => {
  const {
    fhirAccessToken,
    fhirIdToken,
    fhirRefreshToken,
    emrIntegrationDetails
  } = params

  if (isEmpty(fhirAccessToken)) {
    return next(params)
  }

  const remainingSeconds = getTokenExpiryInSeconds(fhirAccessToken)

  // If less than 5 mins left return
  if (remainingSeconds < VALID_ACCESS_TOKEN_TIME_BUFFER) {
    return next(params)
  }

  loSto.set(LO_STO.FHIR_ACCESS_TOKEN, fhirAccessToken)
  loSto.set(LO_STO.FHIR_ID_TOKEN, fhirIdToken)
  loSto.set(LO_STO.EMR_INTEGRATION_DETAILS, {
    ...emrIntegrationDetails,
    status: EMR_INTEGRATION_STATUS.CONNECTED
  })
  pubSub.publish(EMR_INTEGRATION_DETAILS_UPDATE)

  ehrRefreshTokenHandler(emrIntegrationDetails.emr, fhirRefreshToken, remainingSeconds)
}

const isValidRefreshToken = async (next, params = {}) => {
  const {
    fhirRefreshToken,
    emrIntegrationDetails
  } = params

  const { emr, config } = emrIntegrationDetails

  if (isEmpty(fhirRefreshToken)) {
    return next(params)
  }

  // EPIC
  if (emr === SECONDARY_EMR_TYPE.EPIC_EMR) {
    const { access_token, expires_in, error } = await emrIntegrationService.refreshEpicToken(config, fhirRefreshToken)

    if (error) {
      return next(params)
    }
    updateEmrIntegrationDetails(emrIntegrationDetails, access_token, fhirRefreshToken, expires_in)
  }

  // CERNER
  if (emr === SECONDARY_EMR_TYPE.CERNER_EMR) {
    const { access_token, expires_in, error } = await emrIntegrationService.refreshCernerToken(config, fhirRefreshToken)

    if (error) {
      return next(params)
    }
    updateEmrIntegrationDetails(emrIntegrationDetails, access_token, fhirRefreshToken, expires_in)
  }
}

const updateEmrIntegrationFailed = (next, params = {}) => {
  const { emrIntegrationDetails } = params
  logger.error('[Tokens Invalid]')
  loSto.set(LO_STO.EMR_INTEGRATION_DETAILS, {
    ...emrIntegrationDetails,
    status: EMR_INTEGRATION_STATUS.FAILED
  })
  pubSub.publish(EMR_INTEGRATION_DETAILS_UPDATE)
}

const getFhirTokensFromCache = async () => {
  const emrIntegrationDetails = loSto.get(LO_STO.EMR_INTEGRATION_DETAILS) || {}
  const { fhirTokens, error } = await fetchEmrFhirTokens(emrIntegrationDetails.emr)

  if (!isNil(fhirTokens)) {
    const fhirIdToken = fhirTokenCacheReader.fhirIdToken(fhirTokens)
    const fhirAccessToken = fhirTokenCacheReader.fhirAccessToken(fhirTokens)
    const fhirRefreshToken = fhirTokenCacheReader.fhirRefreshToken(fhirTokens)

    const fhirTokenIntegrationProcess = new Process()
      .addHandler(isValidAccessToken)
      .addHandler(isValidRefreshToken)
      .addHandler(updateEmrIntegrationFailed)

    fhirTokenIntegrationProcess.run({
      fhirIdToken,
      fhirAccessToken,
      fhirRefreshToken,
      emrIntegrationDetails
    })
  } else {
    logger.error('[getFhirTokensFromCache Token from redis cache]:', error)
    loSto.set(LO_STO.EMR_INTEGRATION_DETAILS, {
      ...emrIntegrationDetails,
      status: EMR_INTEGRATION_STATUS.FAILED
    })
    pubSub.publish(EMR_INTEGRATION_DETAILS_UPDATE)
  }
}

export { getFhirTokensFromCache }
